import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight
} from 'lucide-react';
import React, { useEffect } from 'react';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { EnrichedTrack } from '../types';
import { fmtTime } from '../utils/format';

interface TimelineScrubberProps {
    progress: number;
    onProgressChange: (progress: number) => void;
    focusedTrack: EnrichedTrack | null;
}

const currentIdx = (track: EnrichedTrack, progress: number): number => {
    const n = track.points.length;
    if (n === 0) return 0;
    return Math.min(n - 1, Math.max(0, Math.floor(progress * (n - 1))));
};

const stepProgress = (
    progress: number,
    track: EnrichedTrack | null,
    delta: number
): number => {
    if (!track || track.points.length <= 1) return progress;
    const n = track.points.length;
    const idx = currentIdx(track, progress);
    const nextIdx = Math.min(n - 1, Math.max(0, idx + delta));
    return nextIdx / (n - 1);
};

const ScrubButton = ({
    title,
    disabled,
    onClick,
    children
}: {
    title: string;
    disabled: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClick}
                disabled={disabled}
            >
                {children}
            </Button>
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
    </Tooltip>
);

export function TimelineScrubber({
    progress,
    onProgressChange,
    focusedTrack
}: TimelineScrubberProps) {
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const tag = target?.tagName ?? '';
            if (tag === 'INPUT' || tag === 'TEXTAREA') return;

            const step = e.shiftKey ? 10 : 1;
            if (e.key === 'ArrowLeft') {
                onProgressChange(stepProgress(progress, focusedTrack, -step));
                e.preventDefault();
            } else if (e.key === 'ArrowRight') {
                onProgressChange(stepProgress(progress, focusedTrack, step));
                e.preventDefault();
            } else if (e.key === 'Home') {
                onProgressChange(0);
                e.preventDefault();
            } else if (e.key === 'End') {
                onProgressChange(1);
                e.preventDefault();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [progress, focusedTrack, onProgressChange]);

    const point = focusedTrack
        ? focusedTrack.points[currentIdx(focusedTrack, progress)]
        : null;

    const indexLabel = focusedTrack
        ? `${currentIdx(focusedTrack, progress) + 1} / ${focusedTrack.points.length}`
        : '—';

    const disabled = !focusedTrack;

    return (
        <Card className="sticky top-0 z-10 py-3">
            <CardContent className="flex items-center gap-2 px-4">
                <ScrubButton
                    title="First (Home)"
                    disabled={disabled}
                    onClick={() => onProgressChange(0)}
                >
                    <ChevronsLeft />
                </ScrubButton>
                <ScrubButton
                    title="Prev (◀)  ·  Shift+◀ × 10"
                    disabled={disabled}
                    onClick={() =>
                        onProgressChange(
                            stepProgress(progress, focusedTrack, -1)
                        )
                    }
                >
                    <ChevronLeft />
                </ScrubButton>

                <input
                    type="range"
                    min={0}
                    max={100}
                    step={0.01}
                    value={progress * 100}
                    onChange={(e) =>
                        onProgressChange(Number(e.target.value) / 100)
                    }
                    className="mx-2 flex-1 accent-primary"
                    aria-label="Timeline position"
                />

                <ScrubButton
                    title="Next (▶)  ·  Shift+▶ × 10"
                    disabled={disabled}
                    onClick={() =>
                        onProgressChange(
                            stepProgress(progress, focusedTrack, 1)
                        )
                    }
                >
                    <ChevronRight />
                </ScrubButton>
                <ScrubButton
                    title="Last (End)"
                    disabled={disabled}
                    onClick={() => onProgressChange(1)}
                >
                    <ChevronsRight />
                </ScrubButton>

                <div className="min-w-[280px] font-mono text-xs text-muted-foreground">
                    <span className="block">
                        {point ? fmtTime(point.t) : '—'}
                    </span>
                    <span>
                        Point {indexLabel} · {(progress * 100).toFixed(1)}%
                    </span>
                </div>
            </CardContent>
        </Card>
    );
}
